<script setup lang="ts">
import Button from '@components/Button.vue';
import Icon from '@components/Icon.vue';
import SearchLink from '@app/layout/nav/desktop/components/SearchLink.vue';
import Typography from '@components/Typography.vue';
import { trans, transchoice } from '@tools/translation.ts';
import { computed } from 'vue';
import IconCaretDown from '@icons/ph_caret-down.svg';
import IconPhoneCall from '@icons/ph_phone-call.svg';
import IconList from '@icons/ph_list.svg';
import IconUserCircleFill from '@icons/ph_user-circle-fill.svg';
import { useNavbarStore } from '@stores/layout/useNavbarStore';
import { storeToRefs } from 'pinia';
import { URL_OFFER_CREATE } from '@tools/routes/routes';
import TooltipClick from '@components/TooltipClick.vue';
import DesktopAnonymousMenu from '@app/layout/nav/desktop/components/menus/DesktopAnonymousMenu.vue';

const companyName = computed<string>(() => window.COMPANY_NAME);

const navbarStore = useNavbarStore();
const { toggleTopPanel, closeTopPanel } = navbarStore;
const { isTopPanelOpen, salesPhone, internationalSalesPhone } = storeToRefs(navbarStore);
</script>

<template>
  <div class="flex items-center justify-between">
    <div class="flex items-center gap-x-4">
      <SearchLink
        class="h-10 pl-3"
        data-cy-ref="top-navbar-desktop__link--search"
      />

      <Button
        variant="invisible"
        :class="[
          'top-navbar-desktop-anonymous__menu-item',
          '!h-10',
          '!px-3 !py-2',
          'flex-1',
          'group',
        ]"
        data-cy-ref="top-navbar-desktop__btn--locations"
        @click.stop="toggleTopPanel"
      >
        <Typography
          variant="body-bold"
          color="gray-darker"
          class="whitespace-nowrap group-hover:text-black"
        >
          {{ trans('user_menu.top.locations' ) }}
        </Typography>

        <Icon
          :svg="IconCaretDown"
          height="24"
          width="24"
          fill="current"
          :class="[
            'transition-transform',
            'duration-200',
            { 'rotate-180': isTopPanelOpen },
            isTopPanelOpen ? 'text-black' : 'text-gray-darker',
            'group-hover:text-black',
          ]"
        />
      </Button>
    </div>

    <div class="flex items-center gap-x-4">
      <a
        :href="`tel:${internationalSalesPhone}`"
        class="top-navbar-desktop-anonymous__menu-item group"
        data-cy-ref="top-navbar-desktop__link--sales-phone"
      >
        <Icon
          :svg="IconPhoneCall"
          height="24"
          width="24"
          fill="current"
          class="text-gray-darker group-hover:text-black"
        />

        <div>
          <Typography
            variant="body-small"
            color="gray-darker"
            class="group-hover:text-black"
          >
            {{ transchoice('user_menu.call_company', { company_name: companyName }) }}
          </Typography>

          <Typography
            variant="body-bold"
            color="gray-darker"
            class="whitespace-nowrap group-hover:text-black"
          >
            {{ salesPhone }}
          </Typography>
        </div>
      </a>

      <a
        :href="URL_OFFER_CREATE"
        :class="[
          'top-navbar-desktop-anonymous__menu-item',
          'h-10',
          'group',
        ]"
        data-cy-ref="top-navbar-desktop__link--create-offer"
      >
        <Typography
          variant="body-bold"
          color="gray-darker"
          class="whitespace-nowrap group-hover:text-black"
        >
          {{ trans('user_menu.main.create_offer') }}
        </Typography>
      </a>

      <TooltipClick panel-class="top-navbar-desktop-anonymous__panel">
        <template #trigger>
          <Button
            :class="[
              '!rounded-4xl',
              '!h-10',
              '!p-2',
              '!flex gap-x-1',
              'hover:!bg-transparent hover:shadow-[0_2px_4px_0_rgba(0,0,0,0.10)]',
            ]"
            data-cy-ref="top-navbar-desktop__btn--user"
            @click="closeTopPanel"
          >
            <Icon
              :svg="IconList"
              height="24"
              width="24"
              fill="gray-darker"
            />

            <Icon
              :svg="IconUserCircleFill"
              height="24"
              width="24"
              fill="gray-darker"
            />
          </Button>
        </template>

        <template #panel="{ close }">
          <DesktopAnonymousMenu
            data-cy-ref="top-navbar-desktop__panel--user"
            @close="close"
          />
        </template>
      </TooltipClick>
    </div>
  </div>
</template>

<style scoped lang="scss">
.top-navbar-desktop-anonymous {
  &__menu-item {
    @apply rounded-lg;

    @apply px-3 py-0;

    @apply flex items-center gap-x-2;

    @apply no-underline;

    @apply hover:bg-blue-lightest;
  }
}

:deep(.top-navbar-desktop-anonymous__panel) {
  @apply mt-2;

  @apply right-0;

  @apply rounded-lg;

  @apply px-0 py-4;
}
</style>
